import React, { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { X } from "lucide-react";
import { getOrderCycle } from "@/lib/orderCycle";

/**
 * Site-wide announcement bar — sits above the sticky Header.
 *
 * Single mode: standard weekly cycle.
 *   • Pre-cutoff: "Free delivery Perth metro · Order by Thu 11pm for Saturday delivery"
 *   • Post-cutoff: "Ordering now? You're first in line for next Saturday <date>."
 *
 * Dismissal persists 14 days. Blocked on cart/checkout/admin/pack/deliver/ops
 * routes + the Bunbury landing (which has its own cycle band).
 */
const DISMISS_KEY = "tbb_announce_dismissed_at";
const DISMISS_DAYS = 14;

function dismissedFresh() {
  try {
    const raw = localStorage.getItem(DISMISS_KEY);
    if (!raw) return false;
    const then = new Date(raw).getTime();
    if (Number.isNaN(then)) return false;
    return (Date.now() - then) < DISMISS_DAYS * 86400 * 1000;
  } catch { return false; }
}

export default function AnnouncementBar() {
  const location = useLocation();
  const [cycle, setCycle] = useState(() => getOrderCycle());
  const [dismissed, setDismissed] = useState(() => dismissedFresh());

  // Retick the cycle every second so we cross the Thu 23:00 boundary
  // without a page reload.
  useEffect(() => {
    const id = setInterval(() => setCycle(getOrderCycle()), 1000);
    return () => clearInterval(id);
  }, []);

  const routeBlocked =
    location.pathname.startsWith("/cart") ||
    location.pathname.startsWith("/checkout") ||
    location.pathname.startsWith("/admin") ||
    location.pathname.startsWith("/pack") ||
    location.pathname.startsWith("/deliver") ||
    location.pathname.startsWith("/drive") ||
    location.pathname.startsWith("/ops") ||
    // Bunbury landing has its own monthly cycle band — no double banner.
    location.pathname.startsWith("/meat-delivery-bunbury");

  if (dismissed || routeBlocked) return null;

  const message =
    cycle.mode === "post-cutoff"
      ? (
        <>
          <b>Ordering now?</b> You&apos;re first in line for next <b>{cycle.deliveryDateLabel}</b>.
        </>
      )
      : (
        <>
          <b>Free delivery Perth metro</b> · Order by Thu 11pm for Saturday delivery
        </>
      );

  const onDismiss = () => {
    try { localStorage.setItem(DISMISS_KEY, new Date().toISOString()); }
    catch { /* private mode */ }
    setDismissed(true);
  };

  return (
    <div
      className="bg-[color:var(--tbb-burgundy)] text-white text-[11px] md:text-xs relative z-40"
      data-testid="announcement-bar"
      data-mode={cycle.mode}
      role="status"
      aria-live="polite"
    >
      <div className="tbb-container py-2 pr-8 flex items-center justify-center text-center font-medium tracking-wide">
        <span className="leading-snug">{message}</span>
      </div>
      <button
        type="button"
        onClick={onDismiss}
        className="absolute top-1/2 right-2 -translate-y-1/2 p-1 text-white/80 hover:text-white"
        aria-label="Dismiss announcement"
        data-testid="announcement-dismiss"
      >
        <X size={14} />
      </button>
    </div>
  );
}
